import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, Target, Sparkles, Loader2, MapPin } from "lucide-react";

export default function GeoTeaserPage() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [seen, setSeen] = useState({});

  useEffect(() => {
    api.get(`/public/geophys/teaser/${token}`).then((r) => setData(r.data))
      .catch((e) => setData(e?.response?.status === 410 ? "expired" : null));
  }, [token]);

  const trackTarget = (t) => {
    if (!t?.id || seen[t.id]) return;
    setSeen((s) => ({ ...s, [t.id]: true }));
    api.post(`/public/geophys/teaser/${token}/target-view`, { id: t.id }).catch(() => {});
  };

  if (data === undefined) return <div className="min-h-screen grid place-items-center bg-[#0B1420] text-slate-300"><Loader2 className="animate-spin mr-2" /> Loading…</div>;
  if (data === "expired") return <div className="min-h-screen grid place-items-center bg-[#0B1420] text-slate-300 px-6 text-center" data-testid="geo-teaser-expired">This share link has expired. Please request an updated link.</div>;
  if (data === null) return <div className="min-h-screen grid place-items-center bg-[#0B1420] text-slate-300" data-testid="geo-teaser-invalid">This link is invalid or has been revoked.</div>;

  const targets = data.targets || [];
  return (
    <div className="min-h-screen bg-[#0B1420] text-slate-100" style={{ fontFamily: "IBM Plex Sans, sans-serif" }} data-testid="geo-teaser-page">
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          {data.logo ? <img src={data.logo} alt="logo" className="h-9 max-w-[150px] object-contain" />
            : <Activity className="text-[#C20511]" size={24} />}
          <div className="min-w-0">
            <div className="font-extrabold tracking-tight truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{data.name}</div>
            <div className="text-[11px] text-slate-400">{[data.project_number, data.client_name, data.country].filter(Boolean).join(" · ") || "-"}</div>
          </div>
          <div className="ml-auto text-[11px] uppercase tracking-widest text-[#C20511] font-semibold hidden sm:block">Priority Targets</div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Priority Exploration Targets</h1>
          <p className="text-slate-400 mt-2 text-sm max-w-2xl">A multi-method geophysical interpretation identified the highest-priority targets where independent survey methods coincide spatially - the most reliable indicators for ground follow-up.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Datasets" value={data.total_datasets} />
          <Stat label="Methods" value={(data.methods || []).length} />
          <Stat label="Coincident targets" value={data.coincident_count} />
          <Stat label="Prepared" value={data.generated} />
        </div>

        {data.map && (
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-white" data-testid="geo-teaser-map">
            <img src={data.map} alt="multi-method target map" className="w-full object-contain" />
          </div>
        )}

        {data.terrain_available && <TerrainThumb token={token} />}

        {targets.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-[#C20511] mb-3" style={{ fontFamily: "Manrope, sans-serif" }}><Target size={16} /> TOP TARGETS</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="geo-teaser-targets">
              {targets.map((t, i) => (
                <div key={t.id || i} onClick={() => trackTarget(t)} role="button" tabIndex={0}
                  className={`rounded-xl border p-4 cursor-pointer transition-colors ${seen[t.id] ? "border-[#C20511]/70 bg-[#C20511]/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                  data-testid={`geo-teaser-target-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-extrabold" style={{ fontFamily: "Manrope, sans-serif" }}>{t.id}</div>
                    <div className="text-2xl font-extrabold text-[#C20511]">{t.score ?? "-"}</div>
                  </div>
                  <div className="text-xs text-slate-400">score / 100</div>
                  <div className="mt-2 text-sm text-slate-200">{(t.methods || []).join(" + ")}</div>
                  <div className="mt-1 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#C20511]/20 text-[#C20511]">{t.priority || "-"} priority</div>
                  <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400"><MapPin size={11} /> {Number(t.lat).toFixed(4)}, {Number(t.lon).toFixed(4)}</div>
                  {t.note && <div className="mt-2 text-[11px] text-slate-300 italic border-l-2 border-[#C20511]/60 pl-2" data-testid={`geo-teaser-note-${i}`}>{t.note}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {data.ai_summary && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-5" data-testid="geo-teaser-summary">
            <div className="flex items-center gap-2 text-sm font-bold text-[#C20511] mb-2" style={{ fontFamily: "Manrope, sans-serif" }}><Sparkles size={15} /> INTERPRETATION SUMMARY</div>
            <p className="text-sm text-slate-300 leading-relaxed">{data.ai_summary}</p>
          </div>
        )}

        <p className="text-[11px] text-slate-500 text-center pt-2 pb-8 border-t border-white/10">
          CONFIDENTIAL - Geophysical anomalies / exploration targets requiring field verification; not a statement of Mineral Resources or Reserves. &nbsp;·&nbsp; Powered by <span className="text-[#C20511] font-semibold">{data.company || "SYMIN"}</span>
        </p>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-white/10 bg-white/5 p-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
    <div className="text-xl font-extrabold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>{value ?? "-"}</div>
  </div>
);

function TerrainThumb({ token }) {
  const [state, setState] = useState("loading");
  if (state === "error") return null;
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/public/geophys/teaser/${token}/terrain`;
  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-white relative" data-testid="geo-teaser-terrain">
      <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-widest text-[#C20511] font-semibold">Site Terrain (3D)</div>
      {state === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85 text-slate-400 text-xs gap-2">
          <Loader2 className="animate-spin" size={18} /> Rendering site terrain…
        </div>
      )}
      <img src={src} alt="3D site terrain" className="w-full object-contain min-h-[140px]" onLoad={() => setState("ok")} onError={() => setState("error")} />
    </div>
  );
}
